import { InterventionTag, SubSector } from '@/types';

const legacyInterventionTypes = new Set(['assumption', 'assumption-growth']);

// Ambition order, lowest first — the axis the level-plus-above rule walks.
const interventionTags: InterventionTag[] = [
  'current',
  'intermediate',
  'aggressive',
];

// The legacy implicit rule as an explicit set: an intervention at a level
// applies at that level and every higher ambition. The FE's default when a
// level is picked on the tag-set control (smooth UX — untouched
// interventions behave exactly as today); never derived on the wire.
const levelAndAbove = (tag: InterventionTag): InterventionTag[] =>
  interventionTags.slice(interventionTags.indexOf(tag));

// The lowest included level — what the level select displays for any set
// (`undefined` for an empty set).
const minTag = (tags: InterventionTag[]): InterventionTag | undefined =>
  interventionTags.find((tag) => tags.includes(tag));

// The FALLBACK targeting offer: the legacy 12, which is what a legacy
// pathway-scenario's baselines can carry. New-model pathway pages pass
// the basis's own sub-sectors instead (see `pathway.tsx`) — since the
// intent shape widened to the ledger axis those need no narrowing, and
// this list is only what the editors fall back to when no evaluation has
// resolved yet.
const targetableSubSectors: SubSector[] = [
  'multi-unit-dwellings',
  'single-dwellings',
  'commercial',
  'retail',
  'health',
  'education',
  'manufacturing',
  'agriculture',
  'forestry',
  'air-travel',
  'rail-freight',
  'marine-freight',
];

const interventionOptionsByCategory: any = {
  development: [
    { value: 'assumption', label: 'Assumption (absolute values)' },
    { value: 'assumption-growth', label: 'Assumption (growth rate)' },
    { value: 'assumption-percentage', label: 'Percentage change' },
    {
      value: 'assumption-derived-dwellings',
      label: 'Derived dwellings from population',
    },
  ],
  energy: [
    { value: 'building-efficiency', label: 'Building efficiency' },
    { value: 'grid-decarbonisation', label: 'Grid decarbonisation' },
    {
      value: 'grid-decarbonisation-percentage',
      label: 'Grid decarbonisation (percentage)',
    },
    { value: 'building-electrification', label: 'Building electrification' },
    { value: 'new-building-standards', label: 'New building standards' },
  ],
  transport: [
    {
      value: 'mode-shift-to-public-transport',
      label: 'Mode shift to public transport',
    },
    {
      value: 'mode-shift-to-active-transport',
      label: 'Mode shift to active transport',
    },
    { value: 'electric-vehicle-takeup', label: 'Electric vehicle takeup' },
    {
      value: 'road-to-rail-freight-mode-shift',
      label: 'Road to rail freight mode shift',
    },
    { value: 'rail-freight-efficiency', label: 'Rail freight efficiency' },
    { value: 'air-transport-efficiency', label: 'Air transport efficiency' },
  ],
  waste: [
    { value: 'waste-reduction', label: 'Waste reduction' },
    {
      value: 'waste-diversion-from-landfill',
      label: 'Diversion from landfill',
    },
    { value: 'waste-treatment', label: 'Waste treatment' },
    { value: 'wastewater-treatment', label: 'Wastewater treatment' },
  ],
  ippu: [
    { value: 'industrial-processes', label: 'Industrial-processes reduction' },
    { value: 'product-use', label: 'Product-use reduction' },
  ],
  agriculture: [
    { value: 'livestock', label: 'Livestock reduction' },
    { value: 'horticulture', label: 'Horticulture reduction' },
  ],
  forestry: [{ value: 'exotic-forest-change', label: 'Exotic forest change' }],
};

const creatableInterventionOptionsByCategory: any = Object.fromEntries(
  Object.entries(interventionOptionsByCategory).map(([k, v]) => [
    k,
    (v as { value: string; label: string }[]).filter(
      (o) => !legacyInterventionTypes.has(o.value),
    ),
  ]),
);

const interventionOptions = Object.entries(
  interventionOptionsByCategory,
  //eslint-disable-next-line @typescript-eslint/no-unused-vars
).flatMap(([_, v]) => v);

const creatableInterventionOptions = Object.entries(
  creatableInterventionOptionsByCategory,
  //eslint-disable-next-line @typescript-eslint/no-unused-vars
).flatMap(([_, v]) => v);

export {
  interventionOptionsByCategory,
  interventionOptions,
  creatableInterventionOptionsByCategory,
  creatableInterventionOptions,
  interventionTags,
  levelAndAbove,
  minTag,
  targetableSubSectors,
};
